import { yieldToMainThread } from '@theme/utilities';
import { Component } from '@theme/component';

/**
 * FlyToCart custom element for animating product images to cart
 * This component creates a visual effect of a product "flying" to the cart when added
 *
 * SOLEYA FIX: the animation used to capture its source/destination positions
 * once via IntersectionObserver at connect time. IntersectionObserver's first
 * callback is delivered asynchronously and its timing isn't guaranteed to
 * line up with the header's own state: while the sticky/shrunk header is
 * transitioning (or has just changed size/position on scroll), the observer
 * could fire before or after that change settles, or simply be delayed by
 * the browser during active scrolling. Whatever rect it happened to capture
 * was then used as the permanent target, so once the header was sticky or
 * shrunk the animation would fly toward a stale position, producing jumps,
 * offsets, or a broken-looking path.
 *
 * The fix removes IntersectionObserver from this path entirely. Instead, the
 * destination is resolved to whichever cart icon is currently visible on
 * screen (see `getVisibleCartIcon`, which is robust to a theme rendering more
 * than one candidate element, e.g. separate desktop/mobile/sticky variants),
 * and both the source and destination positions are read directly via
 * `getBoundingClientRect()` immediately before the animation starts, after
 * waiting two animation frames to guarantee layout has settled. This means
 * the coordinates are always live and never cached, regardless of whether
 * the page is at the top, the header is sticky/shrunk, or the user is mid
 * scroll on a collection or product page, on desktop or mobile.
 */

/**
 * Default selector used to find candidate cart icon elements in the header.
 */
const CART_ICON_SELECTOR = '.header-actions__cart-icon';

/**
 * Checks whether an element is genuinely visible to the user right now: not
 * display:none/visibility:hidden/zero-size, and its current bounding box
 * actually overlaps the viewport. This last check is what correctly excludes
 * a cart icon that still exists in the DOM but has been scrolled or
 * transformed off-screen (e.g. a header hidden via a scroll-down transform),
 * which a simple "is it display:none" check would miss.
 * @param {Element | null | undefined} element
 * @returns {element is HTMLElement}
 */
export function isElementCurrentlyVisible(element) {
  if (!(element instanceof HTMLElement) || !element.isConnected) return false;
  if (typeof element.checkVisibility === 'function' && !element.checkVisibility()) return false;

  const rect = element.getBoundingClientRect();
  if (rect.width === 0 || rect.height === 0) return false;

  const viewportWidth = window.innerWidth || document.documentElement.clientWidth;
  const viewportHeight = window.innerHeight || document.documentElement.clientHeight;

  return rect.bottom > 0 && rect.top < viewportHeight && rect.right > 0 && rect.left < viewportWidth;
}

/**
 * Resolves the cart icon that is currently visible on screen. If more than
 * one element matches the selector (e.g. a theme layout with separate
 * desktop/mobile/sticky header markup), the first one that is actually
 * visible wins. Falls back to the first matching element in the DOM if none
 * currently qualify, so callers always get a usable target instead of null.
 * @param {string} [selector]
 * @returns {HTMLElement | null}
 */
export function getVisibleCartIcon(selector = CART_ICON_SELECTOR) {
  const candidates = /** @type {HTMLElement[]} */ ([...document.querySelectorAll(selector)]);
  return candidates.find(isElementCurrentlyVisible) ?? candidates[0] ?? null;
}

class FlyToCart extends Component {
  /** @type {Element} */
  source;

  /** @type {boolean} */
  useSourceSize = false;

  /** @type {Element} */
  destination;

  connectedCallback() {
    super.connectedCallback();

    // Always resolve the destination to whichever cart icon is currently
    // visible right now. If the caller already passed a visible element,
    // keep it; otherwise (not provided, not connected, or scrolled/hidden
    // off-screen) resolve a fresh one live.
    if (!isElementCurrentlyVisible(this.destination)) {
      this.destination = getVisibleCartIcon() ?? this.destination;
    }

    // Wait two animation frames before measuring: the first lets any layout
    // change triggered by this same interaction (e.g. a sticky header
    // becoming active) commit, the second guarantees the measurement below
    // reflects that fully settled, current state rather than an
    // intermediate one.
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        if (!this.isConnected) return;

        // Re-check visibility one last time immediately before measuring,
        // in case the page scrolled or the header changed state during the
        // two frames we just waited.
        if (!isElementCurrentlyVisible(this.destination)) {
          this.destination = getVisibleCartIcon() ?? this.destination;
        }

        if (!this.source || !this.destination) {
          this.remove();
          return;
        }

        const sourceRect = this.source.getBoundingClientRect();
        const destinationRect = this.destination.getBoundingClientRect();

        this.#animate(sourceRect, destinationRect);
      });
    });
  }

  /**
   * Animates the flying thingy along the bezier curve.
   * @param {DOMRect} sourceRect - The live bounding client rect of the source.
   * @param {DOMRect} destinationRect - The live bounding client rect of the destination.
   */
  #animate = async (sourceRect, destinationRect) => {
    //Define bezier curve points
    const startPoint = {
      x: sourceRect.left + sourceRect.width / 2,
      y: sourceRect.top + sourceRect.height / 2,
    };

    const endPoint = {
      x: destinationRect.left + destinationRect.width / 2,
      y: destinationRect.top + destinationRect.height / 2,
    };

    // Position the flying thingy back to the start point
    if (this.useSourceSize) {
      this.style.setProperty('--width', `${sourceRect.width}px`);
      this.style.setProperty('--height', `${sourceRect.height}px`);
    }
    this.style.setProperty('--start-x', `${startPoint.x}px`);
    this.style.setProperty('--start-y', `${startPoint.y}px`);
    this.style.setProperty('--travel-x', `${endPoint.x - startPoint.x}px`);
    this.style.setProperty('--travel-y', `${endPoint.y - startPoint.y}px`);

    await yieldToMainThread();

    await Promise.allSettled(this.getAnimations().map((a) => a.finished));
    this.remove();
  };
}

if (!customElements.get('fly-to-cart')) {
  customElements.define('fly-to-cart', FlyToCart);
}
